import { Application, type Ticker } from 'pixi.js';
import {
  type AgeId,
  type AgeScene,
  type AgeSceneViewModel,
  type SceneEvent,
  type SceneOptions,
  type Viewport,
  createScene,
} from './scene.js';
import type { ParticleTier } from './particles.js';
import { loadGameFonts } from './fonts.js';

/** The full-screen Pixi world view sitting behind the DOM overlay. */
export interface Renderer {
  readonly canvas: HTMLCanvasElement;
  setScene(id: AgeId, options?: SceneOptions): void;
  /** Push the latest selector-derived view model (typically per logic tick). */
  update(vm: AgeSceneViewModel): void;
  /** Fire a one-shot effect (particle burst, etc.). */
  emit(event: SceneEvent): void;
  /** Set the particle-quality tier (settings). */
  setParticleTier(tier: ParticleTier): void;
  destroy(): void;
}

/** Boot the Pixi application and mount its canvas into `mount`. */
export async function createRenderer(mount: HTMLElement): Promise<Renderer> {
  const app = new Application();
  // Load the game typeface alongside Pixi's own init so scene text rasterises
  // in Departure Mono from the very first frame, not a fallback.
  await Promise.all([
    app.init({
      // Track the mount box, not the window: on a phone the mount is CSS-sized to
      // the top half (see style.css), and Pixi renders at the correct resolution
      // into whatever box it's given.
      resizeTo: mount,
      background: 0x1a1b26,
      antialias: true,
      autoDensity: true,
      resolution: window.devicePixelRatio,
    }),
    loadGameFonts(),
  ]);
  app.canvas.dataset.testid = 'pixi-canvas';
  mount.appendChild(app.canvas);

  let scene: AgeScene | null = null;
  let particleTier: ParticleTier = 'high';

  // Scenes size themselves to the render surface (the mount box), not the window,
  // so the top-half phone layout composes inside the canvas rather than off it.
  const viewport = (): Viewport => ({
    width: app.screen.width,
    height: app.screen.height,
  });

  // Continuous motion runs off the Pixi ticker; framerate-independent via dt.
  const animate = (ticker: Ticker): void => {
    scene?.animate(ticker.deltaMS / 1000);
  };
  app.ticker.add(animate);

  // Pause the ticker for a hidden tab — no wasted frames while backgrounded
  // (tech-design §4: "ticker paused for hidden scenes").
  const onVisibility = (): void => {
    if (document.visibilityState === 'hidden') {
      app.ticker.stop();
    } else {
      app.ticker.start();
    }
  };
  document.addEventListener('visibilitychange', onVisibility);

  return {
    canvas: app.canvas,
    setScene(id: AgeId, options?: SceneOptions): void {
      if (scene) {
        app.stage.removeChild(scene.container);
        scene.destroy();
      }
      scene = createScene(id, { ...options, viewport });
      scene.setParticleTier(particleTier);
      app.stage.addChild(scene.container);
    },
    update(vm: AgeSceneViewModel): void {
      scene?.update(vm);
    },
    emit(event: SceneEvent): void {
      scene?.handleEvent(event);
    },
    setParticleTier(tier: ParticleTier): void {
      particleTier = tier;
      scene?.setParticleTier(tier);
    },
    destroy(): void {
      document.removeEventListener('visibilitychange', onVisibility);
      app.ticker.remove(animate);
      scene?.destroy();
      app.destroy(true, { children: true });
    },
  };
}
